import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { formatGBPfromPence } from '@/lib/format';

/**
 * Cashflows return URL for successful payments.
 *
 * We NEVER trust the browser to say "paid" — only the DB state (`payment_status`
 * === 'paid' AND `tickets_created` === true) can display "You're in".
 *
 * Cashflows appends `paymentjobref` and `paymentref` to the return URL. We
 * pass `paymentref` to the backend on every poll so it can do a live retrieve-
 * verify as a fallback for a delayed or lost webhook. That means the moment
 * this page loads, tickets get issued — no waiting on the webhook queue.
 *
 * If we still can't confirm after ~30s of polling, we show a **pending** state
 * (not a false success): the customer knows their entry is being processed
 * and will land in their inbox once confirmed.
 */
const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const orderRef =
    params.get('ordernumber') ||
    params.get('order_ref') ||
    params.get('orderNumber') ||
    '';
  const paymentRef =
    params.get('paymentref') ||
    params.get('paymentReference') ||
    params.get('payment_ref') ||
    '';

  // 'loading' → still polling
  // 'confirmed' → payment paid AND tickets created
  // 'pending' → payment not confirmed yet after polling window (soft state)
  // 'error' → hard error looking up the order
  const [state, setState] = useState({ phase: 'loading', order: null, error: null });

  useEffect(() => {
    if (!orderRef) {
      setState({ phase: 'error', order: null, error: 'Missing order reference in the URL.' });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 15; // ~30s at 2s intervals

    const poll = async () => {
      try {
        const qs = paymentRef ? `?payment_ref=${encodeURIComponent(paymentRef)}` : '';
        const r = await api.get(`/payments/order/${encodeURIComponent(orderRef)}${qs}`);
        if (cancelled) return;
        const o = r.data;
        const confirmed = o.payment_status === 'paid' && o.tickets_created;
        const failed = o.payment_status === 'failed' || o.payment_status === 'cancelled';
        if (confirmed) {
          setState({ phase: 'confirmed', order: o, error: null });
        } else if (failed) {
          // Don't lie: if the backend has marked it failed/cancelled, say so.
          setState({ phase: 'failed', order: o, error: null });
        } else if (attempts >= MAX_ATTEMPTS) {
          // Do NOT claim success. Show a pending state so the customer knows
          // their entry is being processed and to expect the email.
          setState({ phase: 'pending', order: o, error: null });
        } else {
          attempts += 1;
          setTimeout(poll, 2000);
        }
      } catch (err) {
        if (cancelled) return;
        setState({
          phase: 'error',
          order: null,
          error: err?.response?.data?.detail || 'We couldn\u2019t look up your order.',
        });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [orderRef, paymentRef]);

  return (
    <div className="container-pd py-16 max-w-lg" data-testid="payment-success-page">
      <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">Payment</div>

      {state.phase === 'loading' && (
        <div className="mt-6 rounded-2xl border border-pd bg-surface p-6 flex items-center gap-3" data-testid="payment-success-loading">
          <Loader2 size={18} className="animate-spin text-[color:var(--pd-gold)]" />
          <div>
            <div className="font-display text-2xl">Confirming your entry&hellip;</div>
            <p className="text-xs text-muted-sage mt-1">This usually takes a couple of seconds while your bank confirms the payment with us.</p>
          </div>
        </div>
      )}

      {state.phase === 'error' && (
        <div className="mt-6 rounded-2xl border border-[color:var(--pd-danger)]/50 bg-[color:var(--pd-danger)]/10 p-5 text-sm" data-testid="payment-success-error">
          <div className="text-[color:var(--pd-text)] font-medium">We couldn&rsquo;t confirm your entry.</div>
          <p className="text-muted-sage mt-1">{state.error}</p>
          <p className="text-xs text-muted-sage mt-3">If money left your account and you don&rsquo;t see a confirmation email in the next 15 minutes, contact us at <a href="mailto:hello@thepadeldraw.co.uk" className="text-[color:var(--pd-accent-yellow)] underline">hello@thepadeldraw.co.uk</a> and quote reference <span className="font-mono">{orderRef}</span>.</p>
        </div>
      )}

      {state.phase === 'failed' && (
        <div className="mt-6 rounded-2xl border border-[color:var(--pd-danger)]/50 bg-[color:var(--pd-danger)]/10 p-5 text-sm" data-testid="payment-success-failed">
          <div className="text-[color:var(--pd-text)] font-medium">Your payment didn&rsquo;t go through.</div>
          <p className="text-muted-sage mt-1">
            Our processor reported this transaction as {state.order?.payment_status || 'not completed'}. No tickets have been issued and you should not have been charged. If your card statement shows a charge, please contact us at <a href="mailto:hello@thepadeldraw.co.uk" className="text-[color:var(--pd-accent-yellow)] underline">hello@thepadeldraw.co.uk</a> with reference <span className="font-mono">{orderRef}</span> and we&rsquo;ll investigate immediately.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {state.order?.competition_slug ? (
              <Link to={`/competitions/${state.order.competition_slug}`} className="bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 py-2.5 rounded-xl text-sm font-medium">Try again</Link>
            ) : null}
            <Link to="/competitions" className="border border-pd text-[color:var(--pd-text)] px-4 py-2.5 rounded-xl text-sm font-medium">Browse other competitions</Link>
          </div>
        </div>
      )}

      {state.phase === 'pending' && (
        <div className="mt-6 rounded-2xl border border-[color:var(--pd-gold)]/60 bg-surface p-6" data-testid="payment-success-pending">
          <div className="flex items-start gap-3">
            <Clock size={26} className="text-[color:var(--pd-gold)] shrink-0" />
            <div>
              <div className="font-display text-3xl leading-tight">Almost there&hellip;</div>
              <p className="text-sm text-muted-sage mt-2">
                Your payment has been received. We&rsquo;re just waiting on final confirmation from our processor before your tickets are issued &mdash; this usually clears within a few minutes.
              </p>
              <p className="text-xs text-muted-sage mt-3">
                A confirmation email will land in your inbox the moment your tickets are live. Reference <span className="font-mono">{orderRef}</span>.
              </p>
              <p className="text-xs text-muted-sage mt-2">
                Nothing to do on your end. If you don&rsquo;t see the email within 15 minutes, contact us at <a href="mailto:hello@thepadeldraw.co.uk" className="text-[color:var(--pd-accent-yellow)] underline">hello@thepadeldraw.co.uk</a>.
              </p>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to="/account" className="border border-[color:var(--pd-accent-yellow)] text-[color:var(--pd-accent-yellow)] px-4 py-2.5 rounded-xl text-sm font-medium" data-testid="payment-pending-account">Check my account</Link>
            {state.order?.competition_slug ? (
              <Link to={`/competitions/${state.order.competition_slug}`} className="border border-pd text-[color:var(--pd-text)] px-4 py-2.5 rounded-xl text-sm font-medium">Back to competition</Link>
            ) : null}
          </div>
        </div>
      )}

      {state.phase === 'confirmed' && (
        <div className="mt-6 rounded-2xl border border-[color:var(--pd-accent-yellow)]/60 bg-surface p-6" data-testid="payment-success-confirmed">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={26} className="text-[color:var(--pd-accent-yellow)] shrink-0" />
            <div>
              <div className="font-display text-3xl leading-tight">You&rsquo;re in.</div>
              <p className="text-sm text-muted-sage mt-2">
                {state.order?.quantity} ticket{state.order?.quantity === 1 ? '' : 's'} for <span className="text-[color:var(--pd-text)]">{state.order?.competition_title}</span> &middot; <span className="font-mono">{formatGBPfromPence(state.order?.amount_total)}</span>
              </p>
              <p className="text-xs text-muted-sage mt-3">
                A confirmation with your ticket numbers is on its way to your inbox. Reference <span className="font-mono">{orderRef}</span>.
              </p>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to="/account" className="bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 py-2.5 rounded-xl text-sm font-medium" data-testid="payment-success-account">View my entries</Link>
            {state.order?.competition_slug ? (
              <Link to={`/competitions/${state.order.competition_slug}`} className="border border-[color:var(--pd-accent-yellow)] text-[color:var(--pd-accent-yellow)] px-4 py-2.5 rounded-xl text-sm font-medium">Back to competition</Link>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSuccessPage;
